import React, { useEffect, useRef, useState } from 'react';
import { 
    ConnectTacoWebSocket, 
    SetTacoSocketMessageHandler, 
    ClearTacoSocketMessageHandler, 
    DisconnectTacoWebSocket, 
    SendTacoTaps 
} from '../../../utilities/BL/Tacos';
import { GoalBase } from '../../Admin/Commons/GoalBase';


/**
 * COMPONENTE AFICIONADO: TACO TAP CHALLENGE
 */
const Tacos = () => {
    // ==========================================================================
    // ESTADOS DE JUGADOR Y RED
    // ==========================================================================
    const [playerId, setPlayerId] = useState(() => sessionStorage.getItem('taco.player') || '');
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [toastMessage, setToastMessage] = useState('');

    // ==========================================================================
    // ESTADOS DEL JUEGO (Sincronizados con el Servidor)
    // ==========================================================================
    const [matchState, setMatchState] = useState({
        matchId: null,
        phase: 'WAITING', // WAITING, RUNNING, FINISHED
        winner: 'none',
        endsAt: null,
        customConfig: null
    });

    // Estados visuales locales (para respuesta inmediata)
    const [localScore, setLocalScore] = useState(0);
    const [localStep, setLocalStep] = useState(1);
    
    // Referencias de alto rendimiento
    const tacoImageRef = useRef(null);
    const tapBufferRef = useRef({ count: 0, timer: null, isSending: false });

    // Configuración
    const BATCH_SIZE = 5;
    const FLUSH_MS = 120;
    const MAXIMUM_TACO_STEPS = 4;
    const FLOATING_EMOJIS = matchState.customConfig?.floatingEmojis || ['🌮', '🔥', '✨', '👨‍🍳', '🌶️', '🍅'];
    
    // ==========================================================================
    // RUTAS DINÁMICAS
    // Dependiendo de quién se conectó, pintamos a su personaje
    // ==========================================================================
    const ASSETS = {
        character: playerId === 'player2' 
            ? (matchState.customConfig?.player2Avatar ? `${process.env.REACT_APP_API_URL.replace('/api/', '')}${matchState.customConfig.player2Avatar}` : '/img/games/taco/xolo-caricatura-blanco.png') 
            : (matchState.customConfig?.player1Avatar ? `${process.env.REACT_APP_API_URL.replace('/api/', '')}${matchState.customConfig.player1Avatar}` : '/img/games/taco/xolo-caricatura-rojo.png'),
        tacoStepTemplate: (stepNumber) => matchState.customConfig?.foodSteps?.[stepNumber - 1]
            ? `${process.env.REACT_APP_API_URL.replace('/api/', '')}${matchState.customConfig.foodSteps[stepNumber - 1]}`
            : `/img/games/taco/${stepNumber}.png` 
    };

    // Cargar config/tema inicial al montar (incluso si no ha elegido personaje)
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const rId = queryParams.get('roomId') || '';
        const fetchInitialConfig = async () => {
            try {
                const headers = { 'Content-Type': 'application/json' };
                if (rId) {
                    headers['X-Room-Id'] = rId;
                    headers['roomId'] = rId;
                }
                const res = await fetch(`${process.env.REACT_APP_API_URL}Tacos/State`, { headers });
                if (res.ok) {
                    const data = await res.json();
                    setMatchState(prev => ({
                        ...prev,
                        customConfig: data.customConfig
                    }));
                }
            } catch (err) {
                console.error("Error fetching initial config", err);
            }
        };
        fetchInitialConfig();
    }, []);

    // ==========================================================================
    // CONEXIÓN WEBSOCKET
    // ==========================================================================
    useEffect(() => {
        if (!playerId) return;

        const showToast = (m) => { setToastMessage(m); setTimeout(() => setToastMessage(''), 2500); };
        const queryParams = new URLSearchParams(window.location.search);
        const rId = queryParams.get('roomId') || '';

        const fetchInitialState = async () => {
            try {
                const headers = { 'Content-Type': 'application/json' };
                if (rId) {
                    headers['X-Room-Id'] = rId;
                    headers['roomId'] = rId;
                }
                const res = await fetch(`${process.env.REACT_APP_API_URL}Tacos/State`, { headers });
                if (res.ok) {
                    const data = await res.json();
                    setMatchState(prev => ({
                        ...prev,
                        matchId: data.matchId,
                        phase: data.status === 1 ? 'RUNNING' : data.status === 2 ? 'FINISHED' : 'WAITING',
                        winner: data.winner,
                        endsAt: data.endsAt,
                        customConfig: data.customConfig
                    }));
                    
                    const myStats = playerId === 'player1' ? data.player1 : data.player2;
                    if (myStats) {
                        setLocalScore(myStats.tacosCompleted);
                        setLocalStep(myStats.currentStep);
                    }
                }
            } catch (err) {
                console.error("Error fetching initial state", err);
            }
        };

        fetchInitialState();
        
        ConnectTacoWebSocket(playerId, rId, setConnectionStatus, showToast).then(() => {
            SetTacoSocketMessageHandler(handleIncomingMessage);
        });

        return () => {
            ClearTacoSocketMessageHandler();
            DisconnectTacoWebSocket();
            if (tapBufferRef.current.timer) clearTimeout(tapBufferRef.current.timer);
        };
    }, [playerId]);

    // const handleIncomingMessage = (msg) => {
    //     const type = (msg?.type || '').toLowerCase();
    //     const payload = msg?.payload || {};

    //     if (type === 'taco.progress') {
    //         setMatchState(prev => ({
    //             ...prev,
    //             matchId: payload.matchId,
    //             phase: payload.status === 1 ? 'RUNNING' : payload.status === 2 ? 'FINISHED' : 'WAITING',
    //             winner: payload.winner
    //         }));

    //         // Sincronizamos la UI local con la verdad del servidor
    //         const myStats = playerId === 'player1' ? payload.player1 : payload.player2;
    //         if (myStats) {
    //             setLocalScore(myStats.tacosCompleted);
    //             setLocalStep(myStats.currentStep);
    //         }
    //     } else if (type === 'taco.finished') {
    //         setMatchState(prev => ({
    //             ...prev,
    //             phase: 'FINISHED',
    //             winner: payload.winner
    //         }));
    //     }
    // };

    // const handleIncomingMessage = (msg) => {
    //     const type = (msg?.type || '').toLowerCase();
    //     const payload = msg?.payload || {};

    //     // NUEVO: El servidor nos expulsó (alguien más entró o el Admin presionó Emergencia)
    //     if (type === 'kicked_out') {
    //         setPlayerId(''); // Esto fuerza a React a mostrar la pantalla de selección de personaje
    //         sessionStorage.removeItem('taco.player'); // Borramos la memoria
            
    //         // Opcional: Mostrarle una alerta de por qué lo sacaron
    //         alert("Tu sesión fue cerrada o alguien más ocupó este personaje.");
    //         return;
    //     }

    //     if (type === 'taco.progress') {
    //         setMatchState(prev => ({
    //             ...prev,
    //             matchId: payload.matchId,
    //             phase: payload.status === 1 ? 'RUNNING' : payload.status === 2 ? 'FINISHED' : 'WAITING',
    //             winner: payload.winner,
    //             endsAt: payload.endsAt // Guardamos la hora de cierre
    //         }));

    //         const myStats = playerId === 'player1' ? payload.player1 : payload.player2;
    //         if (myStats) {
    //             setLocalScore(myStats.tacosCompleted);
    //             setLocalStep(myStats.currentStep);
    //         }
    //     } else if (type === 'taco.finished') {
    //         setMatchState(prev => ({
    //             ...prev,
    //             phase: 'FINISHED',
    //             winner: payload.winner
    //         }));
    //     }
    // };

    const handleIncomingMessage = (msg) => {
        const type = (msg?.type || '').toLowerCase();
        const payload = msg?.payload || {};

        if (type === 'kicked_out') {
            // 1. NOS DESCONECTAMOS NOSOTROS PRIMERO (Para apagar la auto-reconexión del BL)
            DisconnectTacoWebSocket();
            ClearTacoSocketMessageHandler();

            // 2. Limpiamos las variables
            setPlayerId(''); 
            sessionStorage.removeItem('taco.player'); 
            
            // 3. Mostramos el mensaje sin congelar la pantalla con alert()
            // Usamos tu función showToast (o un setTimeout con alert si prefieres)
            setTimeout(() => {
                alert("Tu sesión fue cerrada o alguien más ocupó este personaje.");
            }, 100);
            
            return;
        }

        if (type === 'taco.progress') {
            setMatchState(prev => ({
                ...prev,
                matchId: payload.matchId,
                phase: payload.status === 1 ? 'RUNNING' : payload.status === 2 ? 'FINISHED' : 'WAITING',
                winner: payload.winner,
                endsAt: payload.endsAt,
                customConfig: payload.customConfig
            }));

            const myStats = playerId === 'player1' ? payload.player1 : payload.player2;
            if (myStats) {
                setLocalScore(myStats.tacosCompleted);
                setLocalStep(myStats.currentStep);
            }
        } else if (type === 'taco.finished') {
            setMatchState(prev => ({
                ...prev,
                phase: 'FINISHED',
                winner: payload.winner,
                customConfig: payload.customConfig
            }));
        }
    };

    // ==========================================================================
    // SISTEMA DE BATCHING (Envío optimizado de Taps)
    // ==========================================================================
    // El celular bloquea los taps leyendo el reloj universal
    const isTimeUpLocally = matchState.endsAt ? (Date.now() >= new Date(matchState.endsAt).getTime()) : false;

    const canTap = Boolean(playerId && matchState.matchId && matchState.phase === 'RUNNING' && matchState.winner === 'none' && !isTimeUpLocally);

    //const canTap = Boolean(playerId && matchState.matchId && matchState.phase === 'RUNNING' && matchState.winner === 'none');

    const flushTapsToServer = async () => {
        const buffer = tapBufferRef.current;
        if (buffer.timer) { clearTimeout(buffer.timer); buffer.timer = null; }
        if (buffer.isSending || buffer.count <= 0 || !matchState.matchId) return;

        const tapsToSend = buffer.count;
        buffer.count = 0;
        buffer.isSending = true;

        try {
            await SendTacoTaps({ matchId: matchState.matchId, playerId, taps: tapsToSend });
        } catch {
            buffer.count += tapsToSend; // Si falla, los regresamos a la cola
        } finally {
            buffer.isSending = false;
        }
    };

    // ==========================================================================
    // LÓGICA DE PARTÍCULAS VISUALES (DOM Directo)
    // ==========================================================================
    const spawnFloatingEmoji = (x, y) => {
        const emoji = document.createElement('div');
        emoji.className = 'taco-fan__emoji';
        emoji.innerText = FLOATING_EMOJIS[Math.floor(Math.random() * FLOATING_EMOJIS.length)];
        emoji.style.setProperty('--target-x', `${(Math.random() - 0.5) * 300}px`);
        emoji.style.setProperty('--target-y', `${-Math.random() * 300 - 50}px`);
        emoji.style.setProperty('--target-rotation', `${(Math.random() - 0.5) * 360}deg`);
        emoji.style.left = `${x}px`;
        emoji.style.top = `${y}px`;
        document.body.appendChild(emoji);
        setTimeout(() => emoji.remove(), 800);
    };

    const spawnTapEffect = (x, y) => {
        const pixel = document.createElement('div');
        pixel.className = 'taco-fan__tap-effect';
        pixel.style.left = `${x}px`;
        pixel.style.top = `${y}px`;
        document.body.appendChild(pixel);
        setTimeout(() => pixel.remove(), 300);
    };

    // ==========================================================================
    // CONTROLADOR PRINCIPAL DEL TAP
    // ==========================================================================
    const handleScreenTap = (event) => {
        if (!canTap) return;

        let tapX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
        let tapY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY;

        // 1. Efectos Visuales
        spawnTapEffect(tapX, tapY);
        if (tapY > window.innerHeight / 2) spawnFloatingEmoji(tapX, tapY);

        if (tacoImageRef.current) {
            tacoImageRef.current.style.transform = 'scale(1.15)';
            setTimeout(() => {
                if (tacoImageRef.current && !tacoImageRef.current.classList.contains('taco-fan__image--slide-out')) {
                    tacoImageRef.current.style.transform = 'scale(1)';
                }
            }, 50);
        }

        // 2. Progreso Visual Local Inmediato (Para que no se sienta lag)
        setLocalStep((prev) => {
            if (prev === MAXIMUM_TACO_STEPS) {
                if (tacoImageRef.current) {
                    tacoImageRef.current.classList.add('taco-fan__image--slide-out');
                    setTimeout(() => {
                        if (tacoImageRef.current) {
                            tacoImageRef.current.classList.remove('taco-fan__image--slide-out');
                            tacoImageRef.current.style.transform = 'scale(1)';
                        }
                        setLocalScore(s => s + 1);
                    }, 300);
                }
                return 1;
            }
            return prev + 1;
        });

        // 3. Cola de envíos al servidor
        const buffer = tapBufferRef.current;
        buffer.count++;

        if (buffer.count >= BATCH_SIZE) {
            flushTapsToServer();
            return;
        }

        if (!buffer.timer) {
            buffer.timer = setTimeout(flushTapsToServer, FLUSH_MS);
        }
    };

    // ==========================================================================
    // RENDERIZADO
    // ==========================================================================
    
    // PANTALLA DE SELECCIÓN DE JUGADOR
    if (!playerId) {
        return <SelectPlayer onPick={(p) => { setPlayerId(p); sessionStorage.setItem('taco.player', p); }} customConfig={matchState.customConfig} />
    }

    const getWinnerMessage = () => {
        if (matchState.winner === 'none') return '';
        if (matchState.winner === 'tie') return '¡ES UN EMPATE!';
        return matchState.winner === playerId ? '¡GANASTE!' : '¡PERDISTE!';
    };

    const fanStyle = matchState.customConfig?.fanBackground 
        ? {
            backgroundImage: `url(${process.env.REACT_APP_API_URL.replace('/api/', '')}${matchState.customConfig.fanBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }
        : {};

    return (
        <div className="taco-fan" style={fanStyle} onMouseDown={handleScreenTap} onTouchStart={handleScreenTap}>
            
            {/* CABECERA: Botón de Salir y Estatus */}
            <header style={{ position: 'absolute', top: 0, left: 0, width: '100%', padding: '15px', display: 'flex', flexDirection: 'column', zIndex: 12 }}> {/* justifyContent: 'space-between', */}
                <img 
                    src={matchState.customConfig?.fanHeaderLogo ? `${process.env.REACT_APP_API_URL.replace('/api/', '')}${matchState.customConfig.fanHeaderLogo}` : "/img/page/xolos-five.png"} 
                    className="LogoADSLxZtudia" 
                    alt="Logo" 
                />

                <div className='d-flex justify-content-between mt-3'>
                    <button 
                        className="btn btn-danger btn-sm fw-bold" 
                        onClick={() => { setPlayerId(''); sessionStorage.removeItem('taco.player'); }}
                    >
                        <i className="bi bi-box-arrow-right"></i> Salir
                    </button>

                    <div className="text-end">
                        <span className="badge bg-dark small d-block mb-1">{playerId.toUpperCase()}</span>
                        <span className={`badge ${connectionStatus === 'connected' ? 'bg-success' : 'bg-warning'} small d-block`}>
                            {connectionStatus === 'connected' && 'Conectado'}
                        </span>
                    </div>
                </div>
            </header>

            {/* MARCADOR LOCAL */}
            {/* <div style={{ position: 'absolute', top: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, textAlign: 'center' }}> */}
                {/* <p style={{ color: '#f1c40f', fontSize: '2rem', textShadow: '2px 2px 0 #000', margin: 0 }}>
                    {localScore} TACOS
                </p> */}
                {!canTap && matchState.phase !== 'FINISHED' && (
                    <div className='taco-fan__Set'>
                        <p className='taco-fan__Set__Text'>¡Prepárate!</p>
                    </div>
                )}
            {/* </div> */}

            {/* MITAD SUPERIOR (Taco) */}
            <div className="taco-fan__zone">
                <img 
                    ref={tacoImageRef}
                    src={ASSETS.tacoStepTemplate(localStep)} 
                    alt={`Paso ${localStep}`} 
                    className="taco-fan__image" 
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>

            {/* MITAD INFERIOR (Personaje) */}
            <div className="taco-fan__zone taco-fan__zone__player">
                <p>¡Tap Tap Tap!</p>
                <div className='taco-fan__player'>
                    <img 
                        src={ASSETS.character} 
                        alt="Taquero" 
                        className="taco-fan__image" 
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
            </div>

            {/* PANTALLA FINAL */}
            {matchState.phase === 'FINISHED' && (
                <div className="taco-fan__overlay">
                    <div className="taco-fan__note">
                        <div className="taco-fan__result-text">
                            <p style={{ color: matchState.winner === playerId ? '#198754' : '#dc3545', fontSize: '1.2rem', marginBottom: '20px' }}>
                                {getWinnerMessage()}
                            </p>
                            {/* <p>Preparaste</p>
                            <p style={{ fontSize: '2.5rem', color: '#e74c3c', margin: '10px 0' }}>{localScore}</p>
                            <p>tacos en total.</p> */}
                        </div>
                    </div>
                </div>
            )}

            {/* ALERTAS */}
            {/* {toastMessage && (
                <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#198754', color: '#fff', padding: '10px 20px', borderRadius: '20px', fontWeight: 'bold', zIndex: 100 }}>
                    {toastMessage}
                </div>
            )} */}

            <p className='FooterCredits' style={{ textAlign: 'center', zIndex: '12', color: '#fff', fontSize: '18px' }}>Hecho con ♥ por Interaktia | 2026</p>
        </div>
    );
};

// ==========================================================================
// PANTALLA DE SELECCIÓN (Similar a Tiro a Gol)
// ==========================================================================
const SelectPlayer = ({ onPick, customConfig }) => {
    return (
        <div style={{ minHeight: '100vh', width: '100%', backgroundColor: '#ffffff' }}>
            <GoalBase classNameApplied={'Fan__Goal__Header'}>
                <div className="Fan__Goal">
                    <p className="Fan__Goal__Title">
                        {customConfig?.selectPlayerMessage || "Selecciona tu personaje"}
                    </p>
                    <div className="Fan__Goal__Players">
                        <button className="Fan__Goal__Players__Buttons" onClick={() => onPick('player1')}>
                        <img 
                            src={customConfig?.player1Avatar ? `${process.env.REACT_APP_API_URL.replace('/api/', '')}${customConfig.player1Avatar}` : '/img/games/taco/xolo-caricatura-rojo.png'} 
                            className='Fan__Goal__Players__Buttons__Image' 
                            alt={customConfig?.player1Name || "Lucho"} 
                        />
                        <span className='Fan__Goal__Players__Buttons__Text'>{customConfig?.player1Name || "Xolo Mayor Rojo"}</span>
                    </button>
                    <button className="Fan__Goal__Players__Buttons" onClick={() => onPick('player2')}>
                        <img 
                            src={customConfig?.player2Avatar ? `${process.env.REACT_APP_API_URL.replace('/api/', '')}${customConfig.player2Avatar}` : '/img/games/taco/xolo-caricatura-blanco.png'} 
                            className='Fan__Goal__Players__Buttons__Image' 
                            alt={customConfig?.player2Name || "Tunita"} 
                        />
                        <span className='Fan__Goal__Players__Buttons__Text'>{customConfig?.player2Name || "Xolo Mayor Blanco"}</span>
                    </button>
                </div>
            </div>
        </GoalBase>
        </div>
    );
};

export { Tacos };